import { message } from 'antd';

import type { ApiErrorDetail } from '@services/apiSchemas';
import i18n from '../i18n/i18n';

interface ApiErrorOptions {
  t?: (key: string, opts?: Record<string, unknown>) => string;
  fallbackMessage?: string;
  onError?: (errorMessage: string, errorCode: string, response: unknown) => void;
}

interface ApiResponseLike {
  success?: boolean;
  message?: string;
  errors?: Record<string, ApiErrorDetail[] | unknown>;
  interpolations?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * 統一處理 API 錯誤響應
 */
export function handleApiError(response: ApiResponseLike, options: ApiErrorOptions = {}): void {
  const { t, fallbackMessage, onError } = options;

  // 獲取翻譯函數
  const translate: (key: string, opts?: Record<string, unknown>) => string =
    t || ((key, opts) => i18n.t(key, opts) as string);

  // 如果有欄位級驗證錯誤，優先顯示
  if (response.errors && typeof response.errors === 'object') {
    const fieldMessages: string[] = [];
    for (const [field, details] of Object.entries(response.errors)) {
      if (Array.isArray(details)) {
        details.forEach((detail: ApiErrorDetail) => {
          const errorKey = `common.error.${detail.message}`;
          const translated = translate(errorKey, detail.interpolations);
          const errorText = translated !== errorKey ? translated : detail.message;
          if (field === 'base') {
            fieldMessages.push(errorText);
          } else {
            const fieldLabel = translate(`common.fields.${field}`, { defaultValue: field });
            fieldMessages.push(`${fieldLabel}: ${errorText}`);
          }
        });
      }
    }

    if (fieldMessages.length > 0) {
      const finalMessage = fieldMessages.join('\n');
      if (onError) {
        onError(finalMessage, response.message ?? '', response);
      } else {
        message.error(finalMessage);
      }
      return;
    }
  }

  // 獲取錯誤訊息（優先使用 response.message）
  const errorCode = response.message || 'internal_server_error';

  // 嘗試從 common.error 命名空間獲取翻譯
  const translationKey = `common.error.${errorCode}`;
  const errorMessage = translate(translationKey, response.interpolations);

  // 如果翻譯 key 沒有改變（表示沒有對應翻譯），使用備用訊息
  const finalMessage = errorMessage === translationKey
    ? (fallbackMessage || errorCode)
    : errorMessage;

  // 執行自訂錯誤處理或顯示錯誤訊息
  if (onError) {
    onError(finalMessage, errorCode, response);
  } else {
    message.error(finalMessage);
  }
}

/**
 * `operatorApiFetch` 對非預期狀態 throw 出的 structured Error。
 * `errorCode` 來自後端 body.message（後端 contract，見 BACKEND.md §6）。
 * `adminApiFetch` 只 throw 401/403 且不掛 errorCode，不適用此型別。
 */
type ApiFetchError = Error & {
  errorCode?: string;
  interpolations?: Record<string, unknown>;
  status?: number;
};

function hasErrorCode(e: unknown): e is ApiFetchError {
  return e instanceof Error
    && 'errorCode' in e
    && typeof (e as ApiFetchError).errorCode === 'string'
    && (e as ApiFetchError).errorCode !== '';
}

/**
 * 處理 caller `.catch` 接到的 error。
 *
 * 兩種路徑：
 * - **structured fetch error**（`operatorApiFetch` 對 401/4xx/5xx 等 throw 出來，帶 `errorCode`
 *   + `interpolations` + `status`）→ 視同 envelope 走 `handleApiError`，讓使用者看到翻譯後的
 *   backend code，而不是被吞成通用「網路錯誤」。
 * - **真正的網路 / 程式錯誤**（純 `new Error(...)`、JSON parse 失敗、未預期 throw 等）
 *   → 顯示 `common.error.network_error` 通用訊息，console.error 留 root cause 給 Sentry / devtools。
 */
export function handleNetworkError(error: unknown, options: ApiErrorOptions = {}): void {
  if (hasErrorCode(error)) {
    handleApiError(
      {
        success: false,
        message: error.errorCode,
        interpolations: error.interpolations,
      },
      options,
    );
    return;
  }

  const { t, onError } = options;
  const translate: (key: string) => string =
    t || ((key) => i18n.t(key) as string);

  const errorMessage = translate('common.error.network_error');

  if (onError) {
    onError(errorMessage, 'network_error', error);
  } else {
    message.error(errorMessage);
  }

  console.error('Network error:', error);
}

/**
 * 統一的 API 請求錯誤處理包裝器
 */
export async function withApiErrorHandling<T extends ApiResponseLike>(
  apiCall: () => Promise<T>,
  options: ApiErrorOptions = {}
): Promise<T | null> {
  try {
    const response = await apiCall();

    if (response.success) {
      return response;
    }
    handleApiError(response, options);
    return null;

  } catch (error) {
    handleNetworkError(error, options);
    return null;
  }
}

/**
 * 統一的錯誤處理函數（自動判斷錯誤類型）
 */
export function handleError(errorOrResponse: unknown, options: ApiErrorOptions = {}): void {
  // 判斷是 Error 實例還是 API 響應
  if (errorOrResponse instanceof Error) {
    handleNetworkError(errorOrResponse, options);
  } else if (errorOrResponse && typeof errorOrResponse === 'object') {
    handleApiError(errorOrResponse as ApiResponseLike, options);
  } else {
    // 未知錯誤類型，當作網路錯誤處理
    const error = new Error(String(errorOrResponse));
    handleNetworkError(error, options);
  }
}

export default {
  handleApiError,
  handleNetworkError,
  withApiErrorHandling,
  handleError,
};
